import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../auth/AuthAtoms';
import { handleRegister } from '../auth/AuthHelper';
import { getGoogleSignInUrl, socialLoginWithApple } from '../api/auth';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Register = () => {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [googleLoading, setGoogleLoading] = useState(false);
    const [appleLoading, setAppleLoading] = useState(false);
    const [, setCurrentUser] = useAtom(currentUserAtom);
    const navigate = useNavigate();

    const handleAppleSignUp = async () => {
        setAppleLoading(true);
        try {
            const result = await socialLoginWithApple();

            if (result.status === 'account_exists') {
                toast.error(result.message || 'An account with this email already exists.');
                return;
            }

            if (result.status === 'username_required') {
                sessionStorage.setItem('apple_identity_token', result._identityToken);
                sessionStorage.setItem('apple_nonce', result._nonce);
                sessionStorage.setItem('social_provider', 'apple');
                navigate('/auth/choose-username');
                return;
            }

            if (!result.AccessToken) {
                toast.error('Apple sign-up failed: no token received');
                return;
            }

            localStorage.setItem('accessToken', result.AccessToken);
            localStorage.setItem('authOrigin', 'apple');
            if (result.RefreshToken) localStorage.setItem('refreshToken', result.RefreshToken);
            if (result.user) {
                setCurrentUser(result.user);
                localStorage.setItem('username', result.user.username);
            }
            toast.success('Account created successfully');
            navigate('/');
        } catch (err) {
            if (err.message !== 'Sign-in cancelled') toast.error('Apple sign-up failed: ' + err.message);
        } finally {
            setAppleLoading(false);
        }
    };

    const handleGoogleSignUp = async () => {
        setGoogleLoading(true);
        try {
            const url = await getGoogleSignInUrl('login');
            window.location.href = url;
        } catch (err) {
            toast.error('Failed to start Google sign-up');
            setGoogleLoading(false);
        }
    };

    const validatePassword = password => {
        const minLength = 8;
        const hasNumber = /\d/.test(password);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);

        const errors = [];
        if (password.length < minLength) errors.push(`Password must be at least ${minLength} characters long`);
        if (!hasNumber) errors.push('Password must contain at least 1 number');
        if (!hasSpecialChar) errors.push('Password must contain at least 1 special character');
        if (!hasUpperCase) errors.push('Password must contain at least 1 uppercase letter');
        if (!hasLowerCase) errors.push('Password must contain at least 1 lowercase letter');

        return errors;
    };

    const handleRegisterFunction = async e => {
        e.preventDefault();

        const passwordErrors = validatePassword(password);
        if (passwordErrors.length > 0) {
            passwordErrors.forEach(error => toast.error(error));
            return;
        }

        try {
            await handleRegister(username, email, password);
            toast.success('Registration successful');
            //window.location.href = '/login';
        } catch (error) {
            toast.error('Registration failed: ' + error.message);
        }
    };

    return (
        <div>
            <h2>Register</h2>
            <button
                className="google-signin-btn"
                onClick={handleGoogleSignUp}
                disabled={googleLoading}
                type="button"
            >
                <img src="/google-logo.svg" alt="Google" className="google-signin-icon" />
                {googleLoading ? 'Redirecting...' : 'Sign up with Google'}
            </button>
            <button
                className="apple-signin-btn"
                onClick={handleAppleSignUp}
                disabled={appleLoading}
                type="button"
            >
                <span className="apple-signin-icon"></span>
                {appleLoading ? 'Signing up...' : 'Sign up with Apple'}
            </button>
            <div className="login-divider">
                <span>or</span>
            </div>
            <p>IMPORTANT: after you register, look for a confirmation email to be able to login</p>
            <form onSubmit={handleRegisterFunction}>
                <label>
                    Username:
                    <input type="text" value={username} onChange={e => setUsername(e.target.value)} required />
                </label>
                <label>
                    Email:
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                </label>
                <label>
                    Password:
                    <input type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                </label>
                <button type="submit">Register</button>
            </form>
            <p className="tc-consent">
                By registering, you agree to our <Link to="/terms">Terms</Link>.
            </p>
            <h3>Password requirements</h3>
            <p>
                <ul>Password minimum length 8 character(s)</ul>
                <ul>Contains at least 1 number</ul>
                <ul>Contains at least 1 special character</ul>
                <ul>Contains at least 1 uppercase letter</ul>
                <ul>Contains at least 1 lowercase letter</ul>
            </p>
        </div>
    );
};

export default Register;
